// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { HubspotPipelineChange, HubspotPipelineDelta } from '@/audit/api/types';
import { parseAndFormatDate } from '@/utilities/date-time';
import { Stack, Typography } from '@mui/material';

interface Props {
  property: HubspotPipelineDelta;
}

function formatPipelineChange(change: {
  pipelineLabel: string;
  pipelineStageLabel: string;
  enabled: boolean;
}) {
  const status = change.enabled ? 'enabled' : 'disabled';
  return `${change.pipelineLabel} → ${change.pipelineStageLabel} (${status})`;
}

function formatDate(value: string | null): string {
  if (!value) return 'None';
  const result = parseAndFormatDate(value);
  return result.value ?? value;
}

function getChangedFields(
  oldVal: HubspotPipelineChange,
  newVal: HubspotPipelineChange
): string[] {
  const changes: string[] = [];

  if (oldVal.pipelineStageLabel !== newVal.pipelineStageLabel) {
    changes.push(
      `Stage: ${oldVal.pipelineStageLabel} → ${newVal.pipelineStageLabel}`
    );
  }
  if (oldVal.enabled !== newVal.enabled) {
    const oldStatus = oldVal.enabled ? 'enabled' : 'disabled';
    const newStatus = newVal.enabled ? 'enabled' : 'disabled';
    changes.push(`Status: ${oldStatus} → ${newStatus}`);
  }
  if (oldVal.startDate !== newVal.startDate) {
    changes.push(
      `Start date: ${formatDate(oldVal.startDate)} → ${formatDate(newVal.startDate)}`
    );
  }
  if (oldVal.lastSyncAt !== newVal.lastSyncAt) {
    changes.push(
      `Last sync: ${formatDate(oldVal.lastSyncAt)} → ${formatDate(newVal.lastSyncAt)}`
    );
  }

  return changes;
}

export function AuditHubspotPipelineDeltaLabel(props: Props) {
  const added = props.property.newValue?.added || [];
  const removed = props.property.newValue?.removed || [];
  const updated = props.property.newValue?.updated || [];

  if (added.length === 0 && removed.length === 0 && updated.length === 0) {
    return null;
  }

  return (
    <Stack>
      <Typography component="span" variant="caption" fontWeight="bold">
        {props.property.name}:
      </Typography>

      {added.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Added:
          </Typography>
          {added.map((item) => (
            <Typography
              key={item.pipelineId}
              variant="caption"
              sx={{ marginLeft: 2 }}
            >
              {formatPipelineChange(item)}
            </Typography>
          ))}
        </Stack>
      )}

      {removed.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Removed:
          </Typography>
          {removed.map((item) => (
            <Typography
              key={item.pipelineId}
              variant="caption"
              sx={{ marginLeft: 2 }}
            >
              {formatPipelineChange(item)}
            </Typography>
          ))}
        </Stack>
      )}

      {updated.length > 0 && (
        <Stack sx={{ marginLeft: 2 }}>
          <Typography component="span" variant="caption" fontWeight="bold">
            Updated:
          </Typography>
          {updated.map((updateItem) => {
            const changes = getChangedFields(updateItem.old, updateItem.new);
            if (changes.length === 0) return null;
            return (
              <Stack key={updateItem.new.pipelineId} sx={{ marginLeft: 2 }}>
                <Typography variant="caption" fontWeight="bold">
                  {updateItem.new.pipelineLabel}:
                </Typography>
                {changes.map((change) => (
                  <Typography
                    key={change}
                    variant="caption"
                    sx={{ marginLeft: 2 }}
                  >
                    {change}
                  </Typography>
                ))}
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
}
